﻿import { showWarningNotification } from "../../Shared/Notifications/notifications";
import { isElement, isHTMLElement } from "../../Shared/typeGuards";
import { HubSpotForm } from "./HubSpotForm";
import { getFieldType } from "./HubSpotFormFieldType";
import { HubSpotFormOptions, isHubSpotFormOptions } from "./Options/HubSpotFormOptions";

export const hubSpotForms = () => {
  const hubSpotFormWrappers = [
    ...document.querySelectorAll<HTMLDivElement>("[data-hubspot-form]"),
  ];

  hubSpotFormWrappers.forEach(formWrapper => {
    if (!isHTMLElement(formWrapper)) {
      return;
    }

    formsObserver.observe(formWrapper, { childList: true, subtree: true });
    createHubSpotForm(formWrapper);

    formWrapper.addEventListener("change", formChangeHandler);
  });
};

/**
 * Method that handles changes made to the form.
 * Add any handlers here.
 */
const mutationHandler = (mutationList: MutationRecord[]) => {
  mutationList.forEach(mutation => {
    switch (getMutationType(mutation)) {
      case "FormAdded": {
        validateForIFrame(mutation);
        handleFileInput(mutation);
        refreshFormState(mutation);
        handleOptions(mutation);
        break;
      }
      case "DependentFieldAdded": {
        handleFileInput(mutation);
        break;
      }
      case "Unknown": {
        console.warn("Unknown mutation");
        break;
      }
    }
  });
};

type HubSpotFormMutationType =
  | "FormAdded"
  | "ValidationError"
  | "DependentFieldAdded"
  | "Unknown";

const getMutationType = (mutationRecord: MutationRecord): HubSpotFormMutationType => {
  if (mutationRecord.type === "childList" && mutationRecord.addedNodes.length === 1) {
    const addedNode = mutationRecord.addedNodes[0];
    if (!isElement(addedNode)) {
      return "Unknown";
    }

    if (addedNode.classList.contains("hs-form")) {
      return "FormAdded";
    } else if (addedNode.classList.contains("hs-error-msgs")) {
      return "ValidationError";
    } else if (addedNode.classList.contains("hs-form-field")) {
      return "DependentFieldAdded";
    }
  }

  return "Unknown";
};

const formsObserver = new MutationObserver(mutationHandler);

const forms = new Map<HTMLDivElement, HubSpotForm>();
const createHubSpotForm = (hubSpotFormWrapper: HTMLDivElement) => {
  if (forms.has(hubSpotFormWrapper)) {
    return;
  }

  forms.set(hubSpotFormWrapper, new HubSpotForm(hubSpotFormWrapper));
};

const getFormWrapperFromTarget = (target: Element) =>
  target.closest<HTMLDivElement>("[data-hubspot-form]");

/**
 * Shows a warning for editors if the embedded form uses an IFrame.
 */
const validateForIFrame = (mutation: MutationRecord) => {
  if (!isElement(mutation.target)) {
    return;
  }

  const formWrapper = getFormWrapperFromTarget(mutation.target);
  if (!formWrapper || !formWrapper.hasAttribute("data-validate-for-iframe")) {
    return;
  }

  for (const addedNode of mutation.addedNodes) {
    // We only check for IFrames that are second degree descendants of the form, to not catch any other IFrames e.g. reCAPTCHA
    if (
      isElement(addedNode) &&
      ((addedNode.tagName === "IFRAME" &&
        addedNode.parentNode?.parentNode === formWrapper) ||
        addedNode.querySelector(".hs-form-iframe"))
    ) {
      showWarningNotification(
        "Detected a HubSpot Form with an IFrame. RAW HTML should be used instead!"
      );
    }
  }
};

/**
 * Handles file input added. Adds "Remove" button.
 */
const handleFileInput = (mutation: MutationRecord) => {
  if (!isElement(mutation.target)) {
    return;
  }

  const formWrapper = getFormWrapperFromTarget(mutation.target);
  if (!formWrapper) {
    return;
  }

  const form = forms.get(formWrapper);
  if (!form) {
    return;
  }

  const addedFileInputs = getFileFields(form);
  addedFileInputs.forEach(fileInput =>
    fileInput.addEventListener("change", () => fileFieldOnChangeHandler(fileInput, form))
  );
};

const getFileFields = (form: HubSpotForm) =>
  form.formFields.filter(field => getFieldType(field) === "file");

const removeButtonClass = "clear-attachments-button";

const fileFieldOnChangeHandler = (fileField: HTMLElement, form: HubSpotForm) => {
  const fileInput = fileField.querySelector("input");
  if (!fileInput) {
    return true;
  }

  if (
    fileInput.value !== "" &&
    !fileInput.parentElement?.querySelector(`.${removeButtonClass}`)
  ) {
    const removeAttachmentsButton = createRemoveAttachmentsButton(fileField, form);
    if (removeAttachmentsButton) {
      fileInput.after(removeAttachmentsButton);
    }
  }
};

const createRemoveAttachmentsButton = (fileField: HTMLElement, form: HubSpotForm) => {
  const removeAttachmentsButton = document.createElement("button");
  removeAttachmentsButton.classList.add(removeButtonClass);

  const icon = document.createElement("img");
  icon.src = "/ui/gfx/hubspotforms/cross.svg";
  removeAttachmentsButton.appendChild(icon);

  const text = document.createElement("span");
  text.textContent = "Remove";
  removeAttachmentsButton.appendChild(text);

  removeAttachmentsButton.addEventListener("click", () => {
    if (form.setFieldValue(fileField, "")) {
      removeAttachmentsButton.parentNode?.removeChild(removeAttachmentsButton);
    }
  });

  return removeAttachmentsButton;
};

const refreshFormState = (mutation: MutationRecord) => {
  if (!isElement(mutation.target)) {
    return;
  }

  const affectedFormWrapper = getFormWrapperFromTarget(mutation.target);
  if (!affectedFormWrapper) {
    return;
  }

  const form = forms.get(affectedFormWrapper);
  if (!form) {
    return;
  }

  if (!form.formDividers.length) {
    return;
  }

  // Validation mutation - skip
  if (
    [...mutation.addedNodes, ...mutation.removedNodes].some(
      addedNode => isElement(addedNode) && addedNode.classList.contains("hs-error-msgs")
    )
  ) {
    return;
  }

  // Refresh which fields should be visible
  form.moveToStep(form.refreshActiveStep());
};

const formChangeHandler = (event: Event) => {
  if (!event.target || !isElement(event.target)) {
    return;
  }

  const formWrapper = getFormWrapperFromTarget(event.target);
  if (!formWrapper) {
    return;
  }

  const formState = forms.get(formWrapper);
  if (!formState) {
    return;
  }

  requestAnimationFrame(() => {
    if (formState.isActiveStepComplete) {
      formState.moveToStep(formState.activeStep + 1);
    }
  });
};

const handleOptions = (mutation: MutationRecord) => {
  if (!mutation.target || !isElement(mutation.target)) {
    return;
  }

  const formWrapper = getFormWrapperFromTarget(mutation.target);
  if (!formWrapper) {
    return;
  }

  // Should apply options only to the first form on the page
  if ([...forms.keys()].indexOf(formWrapper) !== 0) {
    return;
  }

  const form = forms.get(formWrapper);
  if (!form) {
    return;
  }

  const options = getOptions();
  if (!options) {
    return;
  }

  applyHubSpotFormOptions(form, options);
};

const getOptions = (): null | HubSpotFormOptions => {
  const urlOptions = new URLSearchParams(window.location.search).get("options");

  if (!urlOptions) {
    return null;
  }

  try {
    const result = JSON.parse(urlOptions);

    if (isHubSpotFormOptions(result)) {
      return result;
    }
  } catch (exception) {
    console.error(exception);
  }

  return null;
};

const applyHubSpotFormOptions = (form: HubSpotForm, options: HubSpotFormOptions) => {
  for (const fieldName in options) {
    const option = options[fieldName];
    if (!option) {
      continue;
    }

    if (form.setFieldValue(fieldName, option.value)) {
      console.info(`Successfully applied options for the field: ${fieldName}`);

      const dependentFieldsOptions = option.dependentFields;
      if (dependentFieldsOptions) {
        requestAnimationFrame(() =>
          applyHubSpotFormOptions(form, dependentFieldsOptions)
        );
      }
    }
  }
};
